import type React from 'react';
import { useEffect, useState } from 'react';

import { VehicleGroupFilterOverlay } from './VehicleGroupFilterOverlay';
import { Button } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import type { PopperPlacementType } from '@material-ui/core/Popper';

import type { UUID, VehicleGroup } from '@cobliteam/common-types';

import Icon from '@cobliteam/ui/icons';

import Dropdown from '@cobliteam/ui/components/Dropdown';

export interface VehicleGroupFilterProps {
  vehicleGroups: VehicleGroup[];
  appliedVehicleGroups: Set<UUID>;
  isOpen?: boolean;
  placement?: PopperPlacementType;
  title?: string;
  emptyLabel?: string;
  isApplying?: boolean;
  isLoading?: boolean;
  hideEmptyGroups?: boolean;
  applyVehicleGroupsFilter: (appliedVehicleGroups: VehicleGroup[]) => void;
  onDropdownClick?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClear?: () => void;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
const VehicleGroupFilter: React.FC<VehicleGroupFilterProps> = ({
  vehicleGroups,
  appliedVehicleGroups,
  isOpen,
  placement,
  title,
  emptyLabel,
  isApplying,
  isLoading,
  hideEmptyGroups,
  ...props
}) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(() => {
    const groupsById = new Map<UUID, VehicleGroup>();
    vehicleGroups.forEach((group) => {
      if (appliedVehicleGroups.has(group.id)) {
        groupsById.set(group.id, group);
      }
    });
    return groupsById;
  });

  const filteredGroups = hideEmptyGroups ? vehicleGroups.filter((group) => Boolean(group.vehicleIds?.length)) : vehicleGroups;

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) {
      setForceClosed(false);
    }
  }, [forceClosed]);

  useEffect(() => {
    const groupsById = new Map();
    vehicleGroups.forEach((group) => {
      if (appliedVehicleGroups.has(group.id)) {
        groupsById.set(group.id, group);
      }
    });
    setSelectedGroups(groupsById);
  }, [appliedVehicleGroups, vehicleGroups]);

  const getButtonTitle = () => {
    const filterTitle = title || 'Filtrar grupo';
    const numSelected = appliedVehicleGroups.size;

    if (numSelected > 0) {
      return `${filterTitle} (${numSelected})`;
    }
    return emptyLabel ? `${filterTitle} (${emptyLabel})` : filterTitle;
  };

  const updateSelectionChanged = (groupsToBeApplied: VehicleGroup[]) => {
    const hasNotChanged = groupsToBeApplied.every((group) => appliedVehicleGroups.has(group.id)) && groupsToBeApplied.length === appliedVehicleGroups.size;
    setSelectionChanged(!hasNotChanged);
  };

  const handleGroupSelection = (group: VehicleGroup) => {
    const isCurrentlySelected = selectedGroups.has(group.id);
    const updatedSelected = new Map(selectedGroups);
    if (isCurrentlySelected) {
      updatedSelected.delete(group.id);
    } else {
      updatedSelected.set(group.id, group);
    }

    setSelectedGroups(updatedSelected);
    updateSelectionChanged(Array.from(updatedSelected.values()));
  };

  const clearFilters = () => {
    setSelectedGroups(new Map());
    updateSelectionChanged([]);

    if (props.onClear) {
      props.onClear();
    }
  };

  const cancel = () => {
    setForceClosed((prev) => !prev);

    const updatedSelected = new Map();
    vehicleGroups.forEach((group) => {
      if (appliedVehicleGroups.has(group.id)) {
        updatedSelected.set(group.id, group);
      }
    });
    setSelectedGroups(updatedSelected);
    updateSelectionChanged(Array.from(updatedSelected.values()));

    if (props.onCancel) {
      props.onCancel();
    }
  };

  const applyFilter = () => {
    props.applyVehicleGroupsFilter(Array.from(selectedGroups.values()));

    setForceClosed(true);
    setSelectionChanged(false);
  };

  return (
    <>
      <Dropdown
        onOpenChange={() => setForceClosed(false)}
        forceClose={forceClosed}
        overlay={
          <VehicleGroupFilterOverlay
            vehicleGroups={filteredGroups}
            selectedGroupsIds={new Set(selectedGroups.keys())}
            selectionChanged={selectionChanged}
            isApplying={isApplying}
            isLoading={isLoading}
            onGroupSelection={handleGroupSelection}
            onClearFilter={clearFilters}
            onApplyFilter={applyFilter}
            onCancel={cancel}
          />
        }
      >
        <Button variant='text' startIcon={<Icon name='cb-filters-vehicle-group-cb' />} className={cn('px-2 font-bold', isOpen && 'bg-gray-2')} onClick={props.onDropdownClick}>
          {getButtonTitle()}
        </Button>
      </Dropdown>
    </>
  );
};

export default VehicleGroupFilter;
